import { useState } from 'react';

import { useCurrentUser } from '@/react/hooks/useUser';

import { Button, LoadingButton } from '@@/buttons';
import { FormControl } from '@@/form-components/FormControl';
import { Input, Select } from '@@/form-components/Input';
import { TextArea } from '@@/form-components/Input/Textarea';
import { Modal } from '@@/modals';
import { Badge } from '@@/Badge';

import { ActiveAlertRow } from './types';
import { useCreateSilence } from './useCreateSilence';
import { SilenceData } from './silenceTypes';

type Duration = '4h' | '8h' | '1d' | '5d' | 'custom';

const durationOptions = [
  { value: '4h', label: '4 hours' },
  { value: '8h', label: '8 hours' },
  { value: '1d', label: '1 day' },
  { value: '5d', label: '5 days' },
  { value: 'custom', label: 'Custom' },
] as const;

function calculateEndTime(startTime: Date, duration: Duration): Date {
  const startTimeMs = startTime.getTime();

  let durationMs: number;
  switch (duration) {
    case '4h':
      durationMs = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
      break;
    case '8h':
      durationMs = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
      break;
    case '1d':
      durationMs = 24 * 60 * 60 * 1000; // 1 day in milliseconds
      break;
    case '5d':
      durationMs = 5 * 24 * 60 * 60 * 1000; // 5 days in milliseconds
      break;
    case 'custom':
      // For custom, don't modify the end time - return the same time
      return new Date(startTime);
    default:
      durationMs = 0;
      break;
  }

  const endTime = new Date(startTimeMs + durationMs);
  return endTime;
}

interface Props {
  alert: ActiveAlertRow;
  onSave: (silenceData: SilenceData) => void;
  onCancel: () => void;
}

export function SilenceAlertForm({ alert, onSave, onCancel }: Props) {
  const { user } = useCurrentUser();
  const createSilenceMutation = useCreateSilence();

  function createLocalDate(datetimeLocalStr: string): Date {
    const [datePart, timePart] = datetimeLocalStr.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);

    return new Date(year, month - 1, day, hours, minutes);
  }

  function formatForDatetimeLocal(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  const now = new Date();
  const startTime = new Date(now.getTime() + 60 * 1000);

  const [duration, setDuration] = useState<Duration>('4h');
  const initialEndTime = calculateEndTime(startTime, '4h');

  const [formData, setFormData] = useState<SilenceData>({
    comment: `Silencing alert: ${alert.alertName}`,
    createdBy: user?.Username || '', // Automatically set to current user
    startsAt: formatForDatetimeLocal(startTime),
    endsAt: formatForDatetimeLocal(initialEndTime),
    // Automatically set the three required matchers
    matchers: [
      {
        name: 'alertname',
        value: alert.alertName,
        isRegex: false,
        isEqual: true,
      },
      {
        name: 'alert_id',
        value: alert.id,
        isRegex: false,
        isEqual: true,
      },
      {
        name: 'source',
        value: alert.source,
        isRegex: false,
        isEqual: true,
      },
    ],
  });

  // Update end time when duration or start time changes
  function handleDurationChange(newDuration: Duration) {
    setDuration(newDuration);

    if (newDuration !== 'custom') {
      const startTime = createLocalDate(formData.startsAt);
      const endTime = calculateEndTime(startTime, newDuration);
      const formattedEndTime = formatForDatetimeLocal(endTime);

      setFormData((prev) => ({
        ...prev,
        endsAt: formattedEndTime,
      }));
    }
  }

  function handleStartTimeChange(newStartTime: string) {
    setFormData((prev) => ({ ...prev, startsAt: newStartTime }));

    if (duration !== 'custom') {
      const startTime = createLocalDate(newStartTime);
      const endTime = calculateEndTime(startTime, duration);
      setFormData((prev) => ({
        ...prev,
        endsAt: formatForDatetimeLocal(endTime),
      }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await createSilenceMutation.mutateAsync({
        silenceData: formData,
        // TODO: Get the actual alert manager URL from settings
        // For now, using the default internal AlertManager
        alertManagerURL: 'localhost:9093',
      });

      onSave(formData);
    } catch (error) {
      // Error is already handled by the mutation's error handling
      // eslint-disable-next-line no-console
      console.error('Failed to create silence:', error);
    }
  }

  return (
    <>
      <Modal.Header title="Silence Alert" />

      <Modal.Body>
        {/* Silence Details Form */}
        <form onSubmit={handleSubmit} className="form-horizontal">
          <FormControl label="Comment" required inputId="comment">
            <TextArea
              id="comment"
              value={formData.comment}
              onChange={(e) =>
                setFormData({ ...formData, comment: e.target.value })
              }
              placeholder="Reason for silencing this alert..."
              required
              rows={3}
              data-cy="silence-comment-input"
              disabled={createSilenceMutation.isLoading}
            />
          </FormControl>

          <FormControl label="Created By" required inputId="createdBy">
            <Input
              id="createdBy"
              value={formData.createdBy}
              onChange={(e) =>
                setFormData({ ...formData, createdBy: e.target.value })
              }
              placeholder="Your name or username"
              required
              data-cy="silence-created-by-input"
              disabled={createSilenceMutation.isLoading}
            />
          </FormControl>

          <FormControl label="Duration" required inputId="duration">
            <Select
              id="duration"
              value={duration}
              onChange={(e) => {
                handleDurationChange(e.target.value as Duration);
              }}
              options={durationOptions}
              required
              data-cy="silence-duration-select"
              disabled={createSilenceMutation.isLoading}
            />
          </FormControl>

          <FormControl label="Starts At" required inputId="startsAt">
            <Input
              id="startsAt"
              type="datetime-local"
              value={formData.startsAt}
              onChange={(e) => handleStartTimeChange(e.target.value)}
              required
              data-cy="silence-starts-at-input"
              disabled={
                createSilenceMutation.isLoading || duration !== 'custom'
              }
              readOnly={duration !== 'custom'}
            />
          </FormControl>

          <FormControl label="Ends At" required inputId="endsAt">
            <Input
              id="endsAt"
              type="datetime-local"
              value={formData.endsAt}
              onChange={(e) =>
                setFormData({ ...formData, endsAt: e.target.value })
              }
              required
              data-cy="silence-ends-at-input"
              disabled={
                createSilenceMutation.isLoading || duration !== 'custom'
              }
              readOnly={duration !== 'custom'}
            />
          </FormControl>

          {/* Matchers Information (Read-only) */}
          <div className="form-group">
            <label
              className="col-sm-3 col-lg-2 control-label text-left"
              htmlFor="matchers-info"
            >
              Matchers
            </label>
            <div className="col-sm-9 col-lg-10">
              <div id="matchers-info" className="form-control-static">
                <p className="text-muted small mb-3">
                  This silence will match alerts with the following criteria:
                </p>
                {formData.matchers.map((matcher, index) => (
                  <div key={index} className="mb-2">
                    <Badge type="info" className="mr-2">
                      {matcher.name}
                    </Badge>
                    <span className="text-muted">equals</span>
                    <Badge type="muted" className="ml-2">
                      {matcher.value}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="button"
          color="default"
          onClick={onCancel}
          data-cy="silence-cancel-button"
          disabled={createSilenceMutation.isLoading}
        >
          Cancel
        </Button>
        <LoadingButton
          type="button"
          color="primary"
          onClick={handleSubmit}
          data-cy="silence-submit-button"
          isLoading={createSilenceMutation.isLoading}
          loadingText="Creating..."
        >
          Create Silence
        </LoadingButton>
      </Modal.Footer>
    </>
  );
}
